'use client'

import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

export type ScrollableItem = {
  id: string
  value?: unknown
  scrollToTop?: boolean
}

type Options = {
  containerRef?: RefObject<HTMLElement | null>
}

type ItemValues = Map<string, string>
type ItemRefCallback = (element: HTMLElement | null) => void

const HIGHLIGHT_COLOR = '216, 76, 16'
const HIGHLIGHT_HOLD_MS = 900
const HIGHLIGHT_FADE_MS = 700

const serializeValue = (value: unknown) => JSON.stringify(value ?? null)

export function useScrollToNewItem<T extends ScrollableItem>(items: T[], options?: Options) {
  const pathname = usePathname()

  const previousValuesRef = useRef<ItemValues | null>(null)
  const initializedRef = useRef(false)
  const detectionEnabledRef = useRef(true)

  const nodesRef = useRef<Map<string, HTMLElement>>(new Map())
  const callbacksRef = useRef<Map<string, ItemRefCallback>>(new Map())
  const previousOrderRef = useRef<string[]>([])

  const timeoutIdsRef = useRef<number[]>([])
  const animationFrameRef = useRef<number | null>(null)

  const cancelAnimation = useCallback(() => {
    if (animationFrameRef.current === null) return

    window.cancelAnimationFrame(animationFrameRef.current)
    animationFrameRef.current = null
  }, [])

  const clearHighlight = useCallback(() => {
    for (const timeoutId of timeoutIdsRef.current) {
      window.clearTimeout(timeoutId)
    }

    timeoutIdsRef.current = []

    for (const element of nodesRef.current.values()) {
      element.style.removeProperty('transition')
      element.style.removeProperty('box-shadow')
      // element.style.removeProperty('background-color')
    }
  }, [])

  const reset = useCallback(() => {
    cancelAnimation()
    clearHighlight()

    previousValuesRef.current = null
    previousOrderRef.current = []
    initializedRef.current = false

    nodesRef.current.clear()
    callbacksRef.current.clear()
  }, [cancelAnimation, clearHighlight])

  const highlight = useCallback(
    (element: HTMLElement) => {
      clearHighlight()

      element.style.transition = 'none'
      element.style.boxShadow = `0 0 0 3px rgba(${HIGHLIGHT_COLOR}, 0.55)`

      const fadeTimeoutId = window.setTimeout(() => {
        if (!element.isConnected) return

        element.style.transition = [`box-shadow ${HIGHLIGHT_FADE_MS}ms ease-out`].join(', ')
        element.style.boxShadow = `0 0 0 0 rgba(${HIGHLIGHT_COLOR}, 0)`
      }, HIGHLIGHT_HOLD_MS)

      const cleanupTimeoutId = window.setTimeout(() => {
        if (!element.isConnected) return

        element.style.removeProperty('transition')
        element.style.removeProperty('box-shadow')
      }, HIGHLIGHT_HOLD_MS + HIGHLIGHT_FADE_MS)

      timeoutIdsRef.current = [fadeTimeoutId, cleanupTimeoutId]
    },
    [clearHighlight]
  )

  const registerItem = useCallback((id: string): ItemRefCallback => {
    const existingCallback = callbacksRef.current.get(id)

    if (existingCallback) return existingCallback

    const callback: ItemRefCallback = (element) => {
      if (element) {
        nodesRef.current.set(id, element)
      } else {
        nodesRef.current.delete(id)
      }
    }

    callbacksRef.current.set(id, callback)

    return callback
  }, [])

  /**
   * Runs before paint on forward, back and browser-history navigation.
   * Scrolling remains disabled until the next real user interaction.
   */
  useLayoutEffect(() => {
    detectionEnabledRef.current = false
    reset()
  }, [pathname, reset])

  /**
   * Capture phase enables detection before form event handlers update items.
   */
  useEffect(() => {
    const enableDetection = () => {
      detectionEnabledRef.current = true
    }

    document.addEventListener('pointerdown', enableDetection, true)

    document.addEventListener('keydown', enableDetection, true)

    return () => {
      document.removeEventListener('pointerdown', enableDetection, true)

      document.removeEventListener('keydown', enableDetection, true)
    }
  }, [])

  useEffect(() => {
    const currentValues: ItemValues = new Map()
    const currentOrder: string[] = []

    for (const item of items) {
      currentValues.set(item.id, serializeValue(item.value))
      currentOrder.push(item.id)
    }

    if (!detectionEnabledRef.current) {
      previousValuesRef.current = currentValues
      previousOrderRef.current = currentOrder
      initializedRef.current = currentValues.size > 0
      return
    }

    const previousValues = previousValuesRef.current
    const previousOrder = previousOrderRef.current

    if (initializedRef.current && previousValues) {
      let targetId: string | undefined
      let shouldScrollToTop = false
      let hasValueChange = false

      for (const item of items) {
        const previousValue = previousValues.get(item.id)
        const currentValue = currentValues.get(item.id)

        const isAdded = !previousValues.has(item.id)
        const isUpdated = !isAdded && previousValue !== currentValue

        if (!isAdded && !isUpdated) continue

        hasValueChange = true

        if (item.scrollToTop) {
          shouldScrollToTop = true
        } else {
          targetId = item.id
        }
      }

      /*
       * When values did not change, check whether the item order changed.
       * The item with the largest index difference is treated as the dragged item.
       */
      if (!hasValueChange && previousOrder.length === currentOrder.length) {
        let largestIndexDifference = 0
        let movedItemId: string | undefined

        for (let currentIndex = 0; currentIndex < currentOrder.length; currentIndex++) {
          const id = currentOrder[currentIndex]
          const previousIndex = previousOrder.indexOf(id)

          if (previousIndex === -1) continue

          const indexDifference = Math.abs(previousIndex - currentIndex)

          if (indexDifference > largestIndexDifference) {
            largestIndexDifference = indexDifference
            movedItemId = id
          }
        }

        if (largestIndexDifference > 0) {
          targetId = movedItemId
        }
      }

      cancelAnimation()

      if (shouldScrollToTop) {
        animationFrameRef.current = window.requestAnimationFrame(() => {
          animationFrameRef.current = null

          const container = options?.containerRef?.current

          if (container) {
            container.scrollTo({
              top: 0,
              behavior: 'smooth',
            })
          } else {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            })
          }
        })
      } else if (targetId) {
        animationFrameRef.current = window.requestAnimationFrame(() => {
          animationFrameRef.current = null

          const element = nodesRef.current.get(targetId)

          if (!element?.isConnected) {
            nodesRef.current.delete(targetId)
            return
          }

          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })

          highlight(element)
        })
      }
    }

    initializedRef.current = currentValues.size > 0
    previousValuesRef.current = currentValues
    previousOrderRef.current = currentOrder

    for (const id of callbacksRef.current.keys()) {
      if (currentValues.has(id)) continue

      callbacksRef.current.delete(id)
      nodesRef.current.delete(id)
    }

    return cancelAnimation
  }, [items, cancelAnimation, highlight, options?.containerRef])

  useEffect(() => reset, [reset])

  return { registerItem }
}
